<template>
  <AppTooltip location="right" max-width="320" class="AIAssistant__tooltip">
    <template #activator="{ props }">
      <button
        class="AIAssistantConversationsListItem px-3 py-2 rounded"
        :class="{'active' : isActiveConversation}"
        data-locator="conversation-item"
        @click="handleSelectConversation"
        v-bind="props"
      >
        <span class="text-truncate">{{ conversation.title || $t(`common.untitled`) }}</span>
        <span class="text--x-small flex-shrink-0 text-grey-darken-2">{{ relativeTime }}</span>
      </button>
    </template>
    <span>{{ conversation.title || $t(`common.untitled`) }}</span>
  </AppTooltip>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapMutations, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { ISOToShortenedDate, ISOToWeekDay } from '@/common/utils/dates'
import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'
import type { AIAssistantConversation } from '@/types/ai-assistant'


const MINUTES_IN_DAY = 1440 // 24 * 60
const MINUTES_IN_WEEK = 10080 // 7 * 24 * 60

export default defineComponent({
  name: 'AIAssistantConversationsListItem',
  components: {
    AppTooltip,
  },
  props: {
    conversation: {
      type: Object as () => AIAssistantConversation,
      required: true,
    },
  },
  computed: {
    ...mapState('aiAssistant', ['conversationActiveId']),
    isActiveConversation(): boolean {
      return this.conversation.id === this.conversationActiveId
    },
    relativeTime(): string {
      const now = new Date()
      const updatedAt = new Date(this.conversation.updatedAt || now)
      const diffInMinutes = Math.floor((now.getTime() - updatedAt.getTime()) / 60000)

      if (diffInMinutes < 1) {
        return this.$t('common.now')
      }
      
      if (diffInMinutes < MINUTES_IN_DAY) {
        const hours = updatedAt.getHours().toString().padStart(2, '0')
        const minutes = updatedAt.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }
      
      if (diffInMinutes < MINUTES_IN_WEEK) {
        return ISOToWeekDay(this.conversation.updatedAt || '')
      }
      
      return ISOToShortenedDate(this.conversation.updatedAt || '')
    },
  },
  methods: {
    ...mapMutations('aiAssistant', [
      AIAssistantMutationTypes.SET_CONVERSATION_ACTIVE_ID,
      AIAssistantMutationTypes.SET_QUESTION_TYPE,
    ]),
    handleSelectConversation () {
      this.SET_CONVERSATION_ACTIVE_ID(this.conversation.id)
      this.SET_QUESTION_TYPE(this.conversation.type)
    },
  },
})
</script>

<style scoped lang="scss">
.AIAssistantConversationsListItem {
  align-items: center;
  column-gap: 0.5rem;
  display: flex;
  justify-content: space-between;
  min-height: 40px;
  width: 100%;

  &:hover {
    background-color: rgb(var(--v-theme-grey-50));
    text-align: left;
  }

  &.active {
    background-color: #DDD7F5;
  }
}
</style>